import { GhostText } from '@/components/plate-ui/ghost-text';
import { API_BASE_URL } from '@/lib/api-config';
import { copilotState } from '@/lib/copilot-state';
import { CopilotPlugin } from '@udecode/plate-ai/react';

import { createPlatePlugin } from '@udecode/plate/react';

export const createCopilotPlugin = ({
  chapterTitle = '-',
  chapterDescription = '-',
  chapterBeats = [],
  storyId = '-',
  projectId = '-',
  chapterId = '-',
  tenantId = '-',
  accessToken,
}: {
  chapterTitle?: string;
  chapterDescription?: string;
  chapterBeats?: any[];
  storyId?: string;
  projectId?: string;
  chapterId?: string;
  tenantId?: string;
  accessToken?: string;
} = {}) => CopilotPlugin.configure(({ api }) => ({
  options: {
    debounceDelay: 500,
    renderGhostText: GhostText,
    triggerQuery: ({ editor }: any) => {
      try {
        return !editor.selection || !editor.api.isExpanded();
      } catch (e) {
        return true; 
      }
    },
    getPrompt: ({ editor }: any) => {
        try {
            const contextEntry = editor.selection ? editor.api.block({ highest: true }) : null;
            if (!contextEntry) return " ";
            const text = editor.api.string(contextEntry[0]);
            return text || " ";
        } catch (e) {
            return " ";
        }
    },
    completeOptions: {
      api: `${API_BASE_URL}/api/agent/autocomplete`,
      fetch: async (url: string, options: any) => {
        copilotState.setThinking(true);
        const body = options.body ? JSON.parse(options.body as string) : {};
        if (body.prompt) {
          body.cursorContext = body.prompt;
          delete body.prompt;
        }

        const headers = {
            ...options.headers,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        console.log('CopilotPlugin: Final Request Body:', body);

        try {
          const response = await fetch(url, {
            ...options,
            headers,
            body: JSON.stringify(body),
          });

          if (response.ok) {
             const json = await response.json();
             const textContent = json.content || '';
             copilotState.setThinking(false);

             // Create a mock Response that Plate's callCompletionApi can cleanly read.
             // Plate explicitly calls await res.json() expecting an object with a 'text' property.
             return new Response(JSON.stringify({ text: textContent }), {
                 status: 200,
                 headers: { 'Content-Type': 'application/json' }
             });
          }

          copilotState.setThinking(false);
          return response;
        } catch (error) {
          copilotState.setThinking(false);
          console.error('CopilotPlugin: Fetch error', error);
          throw error;
        }
      },
      headers: {
        'x-project-id': projectId,
        'x-story-id': storyId,
        'x-chapter-id': chapterId,
        'x-tenant-id': tenantId,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: {
        chapterTitle,
        chapterDescription,
        chapterBeats,
        aiContext: '-', // TODO: Add dynamic AI context
        maxTokens: 500,
      },
      onFinish: (_prompt: any, completion: any) => {
         console.log('CopilotPlugin: onFinish', { completion });
         // The completion is now just the text content from our mocked response
         if (completion) {
            console.log('CopilotPlugin: Setting block suggestion', completion);
            api.copilot.setBlockSuggestion({ text: completion });
         } else {
            console.warn('CopilotPlugin: No completion received');
         }
      },
    },
  },
  shortcuts: {
    // Disable native 'Tab' accepting so our custom copilotHandlersPlugin handles
    // multi-line text insertion properly.
    accept: {
      keys: 'disable_native_tab',
    },
  },

} as any));

export const copilotHandlersPlugin = createPlatePlugin({
  key: 'copilot-handlers',
  handlers: {
    onKeyDown: (({ editor }: any) => (event: any) => {
        if (
          event.key === 'Tab' &&
          !event.shiftKey &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          const options = editor.getOptions(CopilotPlugin);
          
          if (options && options.suggestionText) {
            event.preventDefault();
            event.stopPropagation();
            
            const textToInsert = options.suggestionText;
            const api = editor.api as any;
            
            // Clear the suggestion state first to remove the ghost text
            if (api.copilot && api.copilot.setBlockSuggestion) {
               api.copilot.setBlockSuggestion({ text: '' });
               if (api.copilot.reset) {
                  api.copilot.reset();
               }
            }

            console.log('Copilot: Inserting multi-line completion text:', textToInsert);

            // Slate's standard insertText doesn't auto-split blocks for raw '\n' characters inside a text node.
            // When multiple paragraphs are returned by the AI, we must manually split and insert them.
            if (textToInsert) {
               const lines = textToInsert.split(/\r?\n/);
               
               editor.tf.withoutNormalizing(() => {
                  for (let i = 0; i < lines.length; i++) {
                     const line = lines[i];
                     if (i > 0) {
                        // After the first line, we insert a break to create a new paragraph block
                        editor.tf.insertBreak();
                     }
                     if (line.length > 0) {
                        editor.tf.insertText(line);
                     }
                  }
               });
            }

            return;
          }
        }
      }) as any,
  },
});
